// "Marcar pagado por fuera": baja el pendiente sin depositar; el motivo queda en la auditoría.
import { useState } from 'react';
import { toast } from 'sonner';
import { userMessageOf } from '@/api/errors';
import { Button, Input, Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMoneyCents } from '@/shared/lib/format/money';
import {
    isPaidOutsideKeyReused,
    useMarkPaidOutside,
    usePaidOutsideDoubt,
    useRefreshPayments,
} from '../../api/commissionQueries';
import { validatePaidOutside, type PaidOutsideErrors } from '../../lib/paymentForms';
import { centsToPesos, pesosToCents, typedPesos } from '../../lib/pesosInput';
import { FIELD, FIELD_ERROR } from '../fieldStyles';
import { doubtModeOf, WriteDoubtNotice } from './WriteDoubtNotice';

const KEY_REUSED =
    'Ya se había registrado un pago por fuera de este vendedor; revisa el pendiente actualizado.';

interface PaidOutsideDialogProps {
    sellerId: string;
    sellerName: string;
    pendingCents: number;
    onClose: () => void;
}

export function PaidOutsideDialog({
    sellerId,
    sellerName,
    pendingCents,
    onClose,
}: PaidOutsideDialogProps) {
    // Tras un fallo sin respuesta clara se reintenta con la misma llave. Lo enviado completo solo lo
    // conoce esta pestaña; tras recargar o en otra, el monto viene precargado y el motivo se escribe.
    const { doubt, expired, settle } = usePaidOutsideDoubt(sellerId);
    const pending = useRefreshPayments();
    const [editing, setEditing] = useState(false);
    const [amount, setAmount] = useState(() => centsToPesos(pendingCents));
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState<PaidOutsideErrors>({});
    const sent = doubt?.body ?? null;
    const recapture = doubt !== null && sent === null;

    // Una duda sin lo enviado obliga a capturar: monto precargado y motivo vacío.
    const [recapturedKey, setRecapturedKey] = useState<string | null>(null);
    if (recapture && recapturedKey !== doubt.key) {
        setRecapturedKey(doubt.key);
        setAmount(centsToPesos(doubt.amountCents ?? pendingCents));
        setReason('');
    }

    // Cerrar el diálogo cierra el intento, salvo con la duda viva.
    const finish = () => {
        settle();
        onClose();
    };

    // Los avisos van en la mutación: corren aunque el diálogo ya se haya cerrado.
    const mark = useMarkPaidOutside({
        onSuccess: () => {
            toast.success('Marcado como pagado por fuera', {
                description: `Se descontó del pendiente de ${sellerName}.`,
            });
            finish();
        },
        onError: (error) => {
            if (isPaidOutsideKeyReused(error)) {
                // Lo registrado con esa llave ya movió el saldo: no se deja el mismo envío listo.
                toast.error(KEY_REUSED);
                finish();
                return;
            }
            setEditing(false);
            toast.error(userMessageOf(error));
        },
    });
    const retrySent = sent !== null && !editing;
    const locked = retrySent || mark.isPending;
    const mode = doubtModeOf({
        inDoubt: doubt !== null,
        busy: mark.isPending || doubt?.sendingHere === true,
        elsewhere: doubt?.inFlightElsewhere === true,
        recapture,
        editing,
    });

    const cancel = () => {
        if (!mark.isPending) finish();
    };

    const editSent = () => {
        if (sent === null) return;
        setAmount(centsToPesos(sent.amountCents));
        setReason(sent.reason);
        setErrors({});
        setEditing(true);
    };

    const submit = () => {
        if (retrySent) {
            mark.mutate({ sellerId, body: sent, shown: doubt });
            return;
        }
        const found = validatePaidOutside(amount, reason, pendingCents);
        setErrors(found);
        const cents = pesosToCents(amount);
        if (found.amount !== undefined || found.reason !== undefined || cents === null) return;
        mark.mutate({
            sellerId,
            body: { amountCents: cents, reason: reason.trim() },
            shown: doubt,
        });
    };

    return (
        <Modal isOpen onClose={cancel} title={`Marcar pagado por fuera · ${sellerName}`} size="sm">
            <form
                noValidate
                className="flex flex-col gap-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
            >
                <Input
                    id="outside-amount"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    label="Monto (MXN)"
                    hint="Por defecto, todo el pendiente. No se deposita ni se le cobra a la cadena."
                    value={retrySent ? centsToPesos(sent.amountCents) : amount}
                    disabled={locked}
                    onChange={(event) => {
                        setAmount(typedPesos(event.target.value));
                    }}
                    error={errors.amount}
                />
                <div>
                    <label htmlFor="outside-reason" className="text-sm font-medium text-foreground">
                        Motivo
                    </label>
                    <textarea
                        id="outside-reason"
                        rows={3}
                        maxLength={500}
                        required
                        className={cn(FIELD, 'mt-1 h-auto py-1')}
                        placeholder="Cómo se le pagó, para la auditoría"
                        aria-invalid={errors.reason ? true : undefined}
                        aria-describedby={errors.reason ? 'outside-reason-error' : undefined}
                        value={retrySent ? sent.reason : reason}
                        disabled={locked}
                        onChange={(event) => {
                            setReason(event.target.value);
                        }}
                    />
                    {errors.reason !== undefined && (
                        <p id="outside-reason-error" className={FIELD_ERROR}>
                            {errors.reason}
                        </p>
                    )}
                </div>
                <WriteDoubtNotice
                    mode={mode}
                    expired={expired}
                    disabled={mark.isPending}
                    onEdit={editSent}
                    extra={
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span>
                                Pendiente actual: <b>{formatMoneyCents(pendingCents)}</b>
                                {pending.refreshing && (
                                    <span className="ml-1 text-muted-foreground">
                                        · actualizando…
                                    </span>
                                )}
                            </span>
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                disabled={mark.isPending}
                                isLoading={pending.refreshing}
                                onClick={pending.refresh}
                            >
                                Revisar el pendiente
                            </Button>
                        </div>
                    }
                />
                <div className="flex justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        disabled={mark.isPending}
                        onClick={cancel}
                    >
                        Cancelar
                    </Button>
                    <Button type="submit" isLoading={mark.isPending}>
                        {retrySent ? 'Reintentar' : 'Marcar pagado'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
